import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { useSEO } from "../hooks/useSEO";

/**
 * Catch-all route.
 *
 * The server now serves index.html for every URL so that client-side routing
 * works on direct load. That means an unknown path reaches the app rather than
 * nginx, and without this route it rendered a blank white page.
 *
 * `noindex` matters here: without it, search engines can index arbitrary
 * mistyped URLs as valid pages, since every URL returns HTTP 200.
 */
export const NotFoundPage = () => {
    const navigate = useNavigate();

    useSEO({
        title: "Page Not Found | BharatHeal",
        description: "The page you are looking for could not be found.",
        robots: "noindex, follow",
    });

    return (
        <>
            <Header onContactClick={() => navigate("/contact")} />
            <main className="pt-20 pb-20">
                <div className="mx-auto flex min-h-[50vh] max-w-2xl flex-col items-center justify-center px-4 text-center">
                    <p className="text-6xl font-bold text-brand-blue">404</p>
                    <h1 className="mt-4 text-3xl font-bold text-brand-navy">
                        We couldn't find that page
                    </h1>
                    <p className="mt-4 text-lg text-gray-600">
                        The page you're looking for may have moved or no longer exists.
                        If you were in the middle of a treatment enquiry, our team can
                        pick it up from here.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <Button asChild size="lg">
                            <Link to="/">Back to home</Link>
                        </Button>
                        <Button asChild variant="outline" size="lg">
                            <Link to="/free-consultation">Book a free consultation</Link>
                        </Button>
                    </div>
                    <div className="mt-10 text-sm text-gray-500">
                        <span>Or jump to </span>
                        <Link to="/treatments" className="text-brand-blue underline">treatments</Link>
                        <span>, </span>
                        <Link to="/blog" className="text-brand-blue underline">our blog</Link>
                        <span>, or </span>
                        <Link to="/contact" className="text-brand-blue underline">contact us</Link>
                        <span>.</span>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
};
